import piexif from 'piexifjs';
import { startOfDay, parseISO, getUnixTime } from 'date-fns';
import { orderPreservingGroupBy } from 'helpers/array-utils';
import { formatDate } from './intl-helper';
import lodashGet from 'lodash/get';
import lodashSetWith from 'lodash/setWith';

export const assetsByDate = ({ assets, sectionDateFormat }) => {
  const pairs = orderPreservingGroupBy(assets, (asset) => {
    const date = startOfDay(parseISO(asset.capturedAt ?? asset.captured_at));
    return getUnixTime(date);
  });

  return pairs.map(([unixStartOfDay, dayAssets]) => {
    const asset = dayAssets[0];
    const date = parseISO(asset.capturedAt ?? asset.captured_at);
    return {
      key: unixStartOfDay,
      title: formatDate(startOfDay(date), sectionDateFormat),
      assets: dayAssets,
    };
  });
};

export const preloadImage = (sources, crossOrigin = null) => {
  const uris = Array.isArray(sources) ? sources : [sources];
  uris.forEach((uri) => {
    const image = new Image();
    if (crossOrigin !== null) {
      image.crossOrigin = crossOrigin;
    }
    image.src = uri;
  });
};

// Wrapper to use FileReader.readAsDataURL as a Promsie
const _FileReaderDataURL = (file) =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      const dataURL = e.target.result;
      resolve(dataURL);
    };

    fileReader.onerror = () => {
      reject(new Error('Error reading file!'));
    };

    fileReader.readAsDataURL(file);
  });

// Wrapper to use Image load as a Promise
const _ImageLoad = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve(image);
    };

    image.onerror = () => {
      reject(new Error('Error loading photo!'));
    };

    image.src = src;
  });

const isArrayOfLength = (arr, n) => Array.isArray(arr) && arr.length === n;

const convertDMSToDegrees = (dms) => {
  if (!isArrayOfLength(dms, 3)) {
    return null;
  }
  const [d, m, s] = dms;
  if (
    !isArrayOfLength(d, 2) ||
    !isArrayOfLength(m, 2) ||
    !isArrayOfLength(s, 2)
  ) {
    return null;
  }
  return d[0] / d[1] + (m[0] / m[1] + s[0] / s[1] / 60) / 60;
};

// Read v1 API params from piexifjs EXIF object.
// Returns { date_uploaded, lat, lon }; any of the fields may be missing.
const readParamsFromExifObj = (exifObj) => {
  const params = {};

  const dict_0 = exifObj['0th'] || {};
  const dict_exif = exifObj['Exif'] || {};
  const dict_gps = exifObj['GPS'] || {};

  const timestamp =
    dict_0[piexif.ImageIFD.DateTime] ||
    dict_exif[piexif.ExifIFD.DateTimeOriginal] ||
    dict_exif[piexif.ExifIFD.DateTimeDigitized];
  if (timestamp) {
    // Format: "YYYY:MM:DD HH:MM:SS"
    const parts = timestamp.replace(/\s+/g, ':').split(':');
    if (isArrayOfLength(parts, 6)) {
      parts[1] -= 1; // Month is zero based
      params.date_uploaded = new Date(...parts).toISOString();
    }
  }

  const lat = dict_gps[piexif.GPSIFD.GPSLatitude];
  const latRef = dict_gps[piexif.GPSIFD.GPSLatitudeRef];
  const lon = dict_gps[piexif.GPSIFD.GPSLongitude];
  const lonRef = dict_gps[piexif.GPSIFD.GPSLongitudeRef];

  if (lat && latRef && lon && lonRef) {
    // Example: lat = [[43, 1], [28, 1], [567599999, 100000000]], latRef = "N"
    const latDeg = convertDMSToDegrees(lat);
    const lonDeg = convertDMSToDegrees(lon);

    if (latDeg !== null && lonDeg !== null) {
      params.lat = latRef === 'S' ? -latDeg : latDeg;
      params.lon = lonRef === 'W' ? -lonDeg : lonDeg;
    }
  }

  return params;
};

// Returns { dataURL, params: { date_uploaded, lat, lon } }
// NOTE: dataURL may be null, in which case the file wasn't resized.
export const processExternalImage = async ({ file, maxDim = 2880 }) => {
  let dataURLFinal = null;
  let params = {};

  try {
    // If image type is image/jpeg, read file and its EXIF
    const dataURLOriginal = await _FileReaderDataURL(file);
    if (file.type !== 'image/jpeg') {
      return {
        params,
        dataURL: dataURLFinal,
      };
    }

    const exifOriginal = piexif.load(dataURLOriginal);
    params = readParamsFromExifObj(exifOriginal);

    // Prepare resized image (if necessary)
    const image = await _ImageLoad(dataURLOriginal);
    const imageW = image.width;
    const imageH = image.height;

    if (imageW <= maxDim && imageH <= maxDim) {
      return { params };
    }

    // --------------------------------------------------
    // NOTE: Browsers are inconsistent applying EXIF rotation on image load.
    // If EXIF orientation exists, we may or may not be able to determine
    // whether rotation is correct (by comparing EXIF size vs image size).
    // If unable to determine, just skip resizing (will happen on the server).

    const [exifOrientation, exifW, exifH] = [
      `0th[${piexif.ImageIFD.Orientation}]`,
      `0th[${piexif.ImageIFD.ImageWidth}]`,
      `0th[${piexif.ImageIFD.ImageLength}]`,
    ].map((path) => lodashGet(exifOriginal, path, null));

    let skipResizing;
    let writeOrientation;

    if (!exifOrientation || exifOrientation === 1) {
      // No rotation to do: should be correct regardless of browser
      skipResizing = false;
      writeOrientation = false;
    } else if ([5, 6, 7, 8].includes(exifOrientation)) {
      // Rotation by 90 deg
      if (!exifW || !exifH || imageW === imageH) {
        // Square image, or EXIF size not present, so can't check for w/h swap
        skipResizing = true;
      } else if (exifW === imageW && exifH === imageH) {
        // Did NOT rotate correctly--resize, but also write rotation to EXIF
        skipResizing = false;
        writeOrientation = true;
      } else if (exifW === imageH && exifH === imageW) {
        // DID rotate correctly--resize, and don't write rotation to EXIF
        skipResizing = false;
        writeOrientation = false;
      } else {
        // Not sure--maybe size missing from EXIF data?
        skipResizing = true;
      }
    } else {
      // Impossible to check correct orientation since w/h don't swap (e.g. 180)
      skipResizing = true;
    }

    if (skipResizing) {
      return { params };
    }
    // --------------------------------------------------

    const scale = Math.min(1, Math.min(maxDim / imageW, maxDim / imageH));
    const scaledW = scale * imageW;
    const scaledH = scale * imageH;

    const canvas = document.createElement('canvas');
    canvas.width = scaledW;
    canvas.height = scaledH;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(image, 0, 0, scaledW, scaledH);
    const dataURLResized = canvas.toDataURL('image/jpeg', 0.8);

    // Write modified EXIF onto the resized image
    const exifFinal = {};
    [
      'GPS',
      `0th[${piexif.ImageIFD.XResolution}]`,
      `0th[${piexif.ImageIFD.YResolution}]`,
      `0th[${piexif.ImageIFD.ResolutionUnit}]`,
      `0th[${piexif.ImageIFD.DateTime}]`,
      `0th[${piexif.ImageIFD.ImageDescription}]`,
      `0th[${piexif.ImageIFD.Make}]`,
      `0th[${piexif.ImageIFD.Model}]`,
      `0th[${piexif.ImageIFD.Artist}]`,
      `Exif[${piexif.ExifIFD.ExifVersion}]`,
      `Exif[${piexif.ExifIFD.DateTimeOriginal}]`,
      `Exif[${piexif.ExifIFD.DateTimeDigitized}]`,
      `Exif[${piexif.ExifIFD.SubSecTime}]`,
      `Exif[${piexif.ExifIFD.SubSecTimeOriginal}]`,
      `Exif[${piexif.ExifIFD.SubSecTimeDigitized}]`,
      `Exif[${piexif.ExifIFD.SubjectArea}]`,
      `Exif[${piexif.ExifIFD.SubjectDistance}]`,
      `Exif[${piexif.ExifIFD.SubjectDistanceRange}]`,
      `Exif[${piexif.ExifIFD.SubjectLocation}]`,
      `Exif[${piexif.ExifIFD.UserComment}]`,
    ].forEach((path) => {
      const val = lodashGet(exifOriginal, path, null);
      if (val !== null && val !== undefined) {
        lodashSetWith(exifFinal, path, val, Object);
      }
    });

    if (writeOrientation) {
      [
        [`0th[${piexif.ImageIFD.Orientation}]`, exifOrientation],
        [`0th[${piexif.ImageIFD.ImageWidth}]`, scale * exifW],
        [`0th[${piexif.ImageIFD.ImageLength}]`, scale * exifH],
      ].forEach(([path, value]) => {
        lodashSetWith(exifFinal, path, value, Object);
      });
    }

    const exifFinalBytes = piexif.dump(exifFinal);

    dataURLFinal = piexif.insert(exifFinalBytes, dataURLResized);
  } catch (e) {
    console.error(e);
  }

  return { params, dataURL: dataURLFinal };
};

export const v1URLsFromAsset = (asset, { optimisticUri = null } = {}) => {
  const result = {};

  if (optimisticUri) {
    result['url_large'] = optimisticUri;
    result['url_medium'] = optimisticUri;
    result['url_small'] = optimisticUri;
  } else {
    (asset.annotatedUris || []).forEach(({ variant, uri }) => {
      switch (variant) {
        case 'ORIGINAL':
          result['url_large'] = uri;
          break;
        case 'MEDIUM':
          result['url_medium'] = uri;
          break;
        case 'THUMBNAIL':
          result['url_small'] = uri;
          break;
        default:
      }
    });
  }

  (asset.originalUris || []).forEach(({ variant, uri }) => {
    switch (variant) {
      case 'ORIGINAL':
        result['url_large_original'] = uri;
        break;
      case 'MEDIUM':
        result['url_medium_original'] = uri;
        break;
      case 'THUMBNAIL':
        result['url_small_original'] = uri;
        break;
      default:
    }
  });
  return result;
};
